import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Search, X, Loader2, MessageSquare, Bookmark, Lock, LockOpen, ShieldCheck, Fingerprint, Share2, Tag, FolderOpen, MoreHorizontal, Pin, Pencil, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import SwipeableBookmarkItem from './SwipeableBookmarkItem';
import BookmarkGroups, { MoveToGroupModal } from './BookmarkGroups';
import BookmarkLabels, { LabelAssignModal } from './BookmarkLabels';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import SwipeableConversationItem from './SwipeableConversationItem';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';
import { toast } from 'sonner';


export default function ConversationSidebar({ 
  conversations, 
  currentConversationId, 
  onLoadConversation, 
  onCreateNewConversation, 
  onDeleteConversation,
  onDeleteAllConversations,
  onBookmarkRemoved,
  bookmarkCount: externalBookmarkCount,
  closeSidebar,
  defaultTab = 'chats',
  onTabChanged,
  chatReady = true,
  isGuest = false,
  onGuestBlock,
  className = "" 
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(
    defaultTab === 'chats' ? 'conversations' : (defaultTab || 'conversations')
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [loadingBookmarks, setLoadingBookmarks] = useState(false);
  const [bookmarksLoaded, setBookmarksLoaded] = useState(false);
  const [bookmarkCountLocal, setBookmarkCountLocal] = useState(0);
  const debounceRef = React.useRef(null);
  const [savedShares, setSavedShares] = useState([]);
  const [sharesLoaded, setSharesLoaded] = useState(false);
  const [bookmarkSubTab, setBookmarkSubTab] = useState('bookmarks');

  // Label assign modal state
  const [labelAssignTarget, setLabelAssignTarget] = useState(null);
  const [allLabels, setAllLabels] = useState([]);
  const longPressTimerRef = useRef(null);

  // Move to group modal state
  const [moveGroupTarget, setMoveGroupTarget] = useState(null);
  const [allGroups, setAllGroups] = useState([]);

  // Bookmark context menu state
  const [bmContextMenu, setBmContextMenu] = useState(null);
  const bmLongPressRef = useRef(null);

  // Fetch all labels for assignment
  const fetchAllLabels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookmark-labels`, { withCredentials: true });
      setAllLabels(res.data.labels || []);
    } catch { /* ignore */ }
  }, []);

  // Fetch all groups for move-to-group
  const fetchAllGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookmark-groups`, { withCredentials: true });
      setAllGroups(res.data.groups || []);
    } catch { /* ignore */ }
  }, []);

  // Handle moving bookmark to group from bookmarks list
  const handleMoveBookmarkToGroup = async (bookmarkId, groupId) => {
    try {
      await axios.put(`${API}/bookmarks/${bookmarkId}/group`, { group_id: groupId }, { withCredentials: true });
      toast.success(groupId ? 'Bookmark moved to group' : 'Bookmark ungrouped');
      // Update bookmark in list with new group_id
      setBookmarks((prev) =>
        prev.map((b) => b.bookmark_id === bookmarkId ? { ...b, group_id: groupId || undefined } : b)
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move bookmark');
    }
    setMoveGroupTarget(null);
  };

  // Bookmark long-press for mobile
  const startBmLongPress = (e, bm) => {
    bmLongPressRef.current = setTimeout(() => {
      e.preventDefault();
      const touch = e.touches?.[0];
      const x = touch?.clientX || e.clientX || 100;
      const y = touch?.clientY || e.clientY || 100;
      fetchAllLabels();
      fetchAllGroups();
      setBmContextMenu({ x, y, bookmark: bm });
    }, 500);
  };

  const cancelBmLongPress = () => {
    clearTimeout(bmLongPressRef.current);
  };

  // Bookmark right-click for desktop
  const handleBmContextMenu = (e, bm) => {
    e.preventDefault();
    fetchAllLabels();
    fetchAllGroups();
    setBmContextMenu({ x: e.clientX, y: e.clientY, bookmark: bm });
  };

  // Vault state
  const [vaultStatus, setVaultStatus] = useState({ has_pin: false, has_biometric: false, private_count: 0 });
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [vaultConversations, setVaultConversations] = useState([]);
  const [vaultLoading, setVaultLoading] = useState(false);
  const vaultTimerRef = useRef(null);

  // Fetch vault status on mount
  useEffect(() => {
    axios.get(`${API}/vault/status`, { withCredentials: true })
      .then(res => setVaultStatus(res.data))
      .catch(() => {});
  }, []);

  // Auto-lock on visibility change + inactivity
  useEffect(() => {
    if (!vaultUnlocked) return;

    const lockVault = () => {
      setVaultUnlocked(false);
      setVaultConversations([]);
      if (activeTab === 'private') {
        setActiveTab('conversations');
        if (onTabChanged) onTabChanged('conversations');
      }
    };

    const resetTimer = () => {
      clearTimeout(vaultTimerRef.current);
      vaultTimerRef.current = setTimeout(lockVault, 2 * 60 * 1000); // 2 minutes
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') lockVault();
    };

    resetTimer();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const events = ['mousedown', 'touchstart', 'keydown', 'scroll'];
    events.forEach(e => document.addEventListener(e, resetTimer, { passive: true }));

    return () => {
      clearTimeout(vaultTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      events.forEach(e => document.removeEventListener(e, resetTimer));
    };
  }, [vaultUnlocked, activeTab, onTabChanged]);

  // Sync defaultTab from parent (e.g. after vault unlock reopens sidebar)
  React.useEffect(() => {
    if (defaultTab === 'private') {
      // Always load vault data when defaultTab is 'private'
      // This handles both: fresh mount with defaultTab='private' AND prop change to 'private'
      setActiveTab('private');
      setVaultUnlocked(true);
      setVaultLoading(true);
      axios.get(`${API}/vault/conversations`, { withCredentials: true })
        .then(res => setVaultConversations(res.data))
        .catch(() => setVaultConversations([]))
        .finally(() => setVaultLoading(false));
    } else if (defaultTab && defaultTab !== activeTab) {
      const mapped = defaultTab === 'chats' ? 'conversations' : defaultTab;
      if (mapped !== activeTab) setActiveTab(mapped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab]);

  // Fetch bookmark count on mount
  React.useEffect(() => {
    axios.get(`${API}/bookmarks`, { withCredentials: true })
      .then(res => {
        const bms = res.data.bookmarks || [];
        setBookmarkCountLocal(bms.length);
        setBookmarks(bms);
        setBookmarksLoaded(true);
      })
      .catch(() => {});
  }, []);

  // Listen for vault-unlocked event from ChatPage
  React.useEffect(() => {
    const handleVaultUnlocked = () => {
      setVaultUnlocked(true);
      setVaultStatus(prev => ({ ...prev, has_pin: true }));
      setActiveTab('private');
      // Load vault conversations
      axios.get(`${API}/vault/conversations`, { withCredentials: true })
        .then(res => setVaultConversations(res.data))
        .catch(() => {});
      // Refresh vault status to pick up biometric changes
      axios.get(`${API}/vault/status`, { withCredentials: true })
        .then(res => setVaultStatus(res.data))
        .catch(() => {});
    };
    const handleVaultConvCreated = (e) => {
      // New private conversation created — add it to the vault list
      if (e.detail) {
        setVaultConversations(prev => [e.detail, ...prev]);
      }
    };
    window.addEventListener('vault-unlocked', handleVaultUnlocked);
    window.addEventListener('vault-conversation-created', handleVaultConvCreated);
    return () => {
      window.removeEventListener('vault-unlocked', handleVaultUnlocked);
      window.removeEventListener('vault-conversation-created', handleVaultConvCreated);
    };
  }, []);

  const displayCount = externalBookmarkCount !== undefined ? externalBookmarkCount : bookmarkCountLocal;

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/conversations/search?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setSearchResults(res.data.results);
    } catch { setSearchResults([]); }
    setSearching(false);
  }, []);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  };

  const clearSearch = () => { setSearchQuery(''); setSearchResults(null); };

  const loadBookmarks = useCallback(async () => {
    if (bookmarksLoaded) return;
    setLoadingBookmarks(true);
    try {
      const res = await axios.get(`${API}/bookmarks`, { withCredentials: true });
      setBookmarks(res.data.bookmarks || []);
      setBookmarksLoaded(true);
    } catch { setBookmarks([]); }
    // Also load saved shares
    if (!sharesLoaded) {
      try {
        const res = await axios.get(`${API}/saved-shares`, { withCredentials: true });
        setSavedShares(res.data.saved_shares || []);
        setSharesLoaded(true);
      } catch { setSavedShares([]); }
    }
    setLoadingBookmarks(false);
    // Preload labels and groups for context menu
    fetchAllLabels();
    fetchAllGroups();
  }, [bookmarksLoaded, sharesLoaded, fetchAllLabels, fetchAllGroups]);

  const handleTabSwitch = (tab) => {
    if (tab === 'private') {
      if (!vaultStatus.has_pin) {
        // Dispatch event to ChatPage to show modal outside the Sheet
        window.dispatchEvent(new CustomEvent('vault-request', { detail: { mode: 'setup' } }));
        return;
      }
      if (!vaultUnlocked) {
        window.dispatchEvent(new CustomEvent('vault-request', { detail: { mode: 'verify' } }));
        return;
      }
    }
    setActiveTab(tab);
    if (onTabChanged) onTabChanged(tab);
    if (tab === 'bookmarks') loadBookmarks();
  };

  const loadVaultConversations = async () => {
    setVaultLoading(true);
    try {
      const res = await axios.get(`${API}/vault/conversations`, { withCredentials: true });
      setVaultConversations(res.data);
    } catch { setVaultConversations([]); }
    setVaultLoading(false);
  };

  const handleMoveToVault = async (conversationId) => {
    if (!vaultStatus.has_pin) {
      toast.error(t('generated.conversationsidebar_set_up_a_c914'));
      window.dispatchEvent(new CustomEvent('vault-request', { detail: { mode: 'setup' } }));
      return;
    }
    try {
      await axios.put(`${API}/conversations/${conversationId}/privacy`, { is_private: true }, { withCredentials: true });
      toast.success(t('generated.conversationsidebar_moved_to_private_c32d'));
      setVaultStatus(s => ({ ...s, private_count: s.private_count + 1 }));
      if (vaultUnlocked) loadVaultConversations();
      // Trigger parent to refresh conversations (remove from regular list)
      if (onDeleteConversation) {
        // We need to refresh the list - simulate by reloading
        window.dispatchEvent(new CustomEvent('vault-conversation-moved'));
      }
    } catch { toast.error(t('generated.conversationsidebar_failed_to_move_7fcc')); }
  };

  const handleMoveFromVault = async (conversationId) => {
    try {
      await axios.put(`${API}/conversations/${conversationId}/privacy`, { is_private: false }, { withCredentials: true });
      toast.success(t('generated.conversationsidebar_moved_back_to_b5ba'));
      setVaultConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
      setVaultStatus(s => ({ ...s, private_count: Math.max(0, s.private_count - 1) }));
      window.dispatchEvent(new CustomEvent('vault-conversation-moved'));
    } catch { toast.error(t('generated.conversationsidebar_failed_to_move_7fcc')); }
  };

  const refreshBookmarks = async () => {
    setLoadingBookmarks(true);
    try {
      const res = await axios.get(`${API}/bookmarks`, { withCredentials: true });
      const bms = res.data.bookmarks || [];
      setBookmarks(bms);
      setBookmarkCountLocal(bms.length);
      setBookmarksLoaded(true);
    } catch { setBookmarks([]); }
    try {
      const res = await axios.get(`${API}/saved-shares`, { withCredentials: true });
      setSavedShares(res.data.saved_shares || []);
      setSharesLoaded(true);
    } catch { setSavedShares([]); }
    setLoadingBookmarks(false);
  };

  const [confirmDeleteBookmark, setConfirmDeleteBookmark] = useState(null);

  const removeBookmark = async (bookmarkId) => {
    const bm = bookmarks.find(b => b.bookmark_id === bookmarkId);
    try {
      await axios.delete(`${API}/bookmarks/${bookmarkId}`, { withCredentials: true });
      setBookmarks(prev => prev.filter(b => b.bookmark_id !== bookmarkId));
      setBookmarkCountLocal(prev => Math.max(0, prev - 1));
      if (bm?.message_id && onBookmarkRemoved) onBookmarkRemoved(bm.message_id);
    } catch {}
  };

  const removeSavedShare = async (saveId) => {
    try {
      await axios.delete(`${API}/saved-shares/${saveId}`, { withCredentials: true });
      setSavedShares(prev => prev.filter(s => s.save_id !== saveId));
      toast.success(t('generated.conversationsidebar_removed_from_saved_54ee'));
    } catch { toast.error(t('generated.conversationsidebar_failed_to_remove_e595')); }
  };

  const refreshSavedShares = async () => {
    try {
      const res = await axios.get(`${API}/saved-shares`, { withCredentials: true });
      setSavedShares(res.data.saved_shares || []);
      setSharesLoaded(true);
    } catch { /* ignore */ }
  };

  const displayConversations = searchResults !== null ? searchResults : conversations;

  const handleTogglePin = async (conversationId, currentlyPinned) => {
    try {
      const res = await axios.put(`${API}/conversations/${conversationId}/pin`, {}, { withCredentials: true });
      // Dispatch event so parent (ChatPage) can refresh conversation list
      window.dispatchEvent(new CustomEvent('conversation-pin-toggled', { detail: { conversation_id: conversationId, is_pinned: res.data.is_pinned } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pin');
    }
  };

  // ── Rename ────────────────────────────────────────────────────────
  // Inline rename: clicking the pencil replaces the row title with an
  // input; Enter (or blur) saves, Escape cancels. We optimistically
  // update the DOM via a dispatched event so parent lists refresh
  // without a round-trip flicker.
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');

  const startRename = (conv) => {
    setRenamingId(conv.conversation_id);
    setRenameDraft(conv.title || '');
  };
  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft('');
  };
  const commitRename = async () => {
    if (!renamingId) return;
    const next = (renameDraft || '').trim();
    if (!next) { cancelRename(); return; }
    const id = renamingId;
    cancelRename();
    try {
      await axios.patch(`${API}/conversations/${id}`,
        { title: next }, { withCredentials: true });
      window.dispatchEvent(new CustomEvent('conversation-renamed',
        { detail: { conversation_id: id, title: next } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename');
    }
  };

  // ── Pagination ────────────────────────────────────────────────────
  // Client-side paginator over the parent-supplied conversation list.
  // Backend supports server-side offset/limit (see /conversations?limit=&offset=)
  // but the sidebar already has the full list in memory for search, so we
  // slice locally to keep instant tab-switching. `pageSize` picker: 5/10/20.
  const [pageSize, setPageSize] = useState(() => {
    const stored = Number(localStorage.getItem('convSidebarPageSize'));
    return [5, 10, 20].includes(stored) ? stored : 10;
  });
  const [page, setPage] = useState(1);
  useEffect(() => { localStorage.setItem('convSidebarPageSize', String(pageSize)); }, [pageSize]);
  // Reset to first page when the base list length or search state changes.
  useEffect(() => { setPage(1); }, [displayConversations.length, searchResults]);

  const pinnedConversations = displayConversations.filter(c => c.is_pinned);
  const unpinnedConversations = displayConversations.filter(c => !c.is_pinned);

  return (
    <div className={`flex flex-col h-full bg-card ${className}`} data-testid="conversation-sidebar">
      <div className="px-4 pt-4 pb-3 border-b border-border space-y-3">
        <Button
          onClick={() => {
            if (activeTab === 'private' && vaultUnlocked) {
              onCreateNewConversation(true);
            } else {
              onCreateNewConversation();
            }
          }}
          disabled={!chatReady}
          className="w-full bg-[var(--theme-accent)] hover:opacity-90"
          style={{ color: 'var(--theme-surface)' }}
          data-testid="new-conversation-button"
        >
          {!chatReady ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('common.loading', 'Loading...')}
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" />
              {activeTab === 'private' && vaultUnlocked ? t('sidebar.new_private_chat', 'New Private Chat') : t('sidebar.new_chat')}
            </>
          )}
        </Button>

        {/* Tabs */}
        <div className="flex gap-0.5 rounded-lg bg-background/40 p-0.5" data-testid="sidebar-tabs">
          <button
            onClick={() => handleTabSwitch('conversations')}
            className={`flex-1 flex items-center justify-center gap-1 rounded-md px-1.5 py-1.5 font-medium transition-all ${
              activeTab === 'conversations' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            style={{ fontSize: '11px' }}
            data-testid="tab-conversations"
          >
            <MessageSquare className="h-3 w-3 shrink-0" />
            <span className="truncate">{t('sidebar.chats', 'Chats')}</span>
          </button>
          <button
            onClick={() => {
              if (isGuest) { onGuestBlock?.('the private vault'); return; }
              handleTabSwitch('private');
            }}
            className={`flex items-center justify-center gap-1 rounded-md px-2.5 py-1.5 font-medium transition-all ${
              activeTab === 'private' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            style={{ fontSize: '11px', ...(isGuest ? { opacity: 0.4 } : {}) }}
            data-testid="tab-private"
          >
            {vaultUnlocked ? <LockOpen className="h-3 w-3 shrink-0 text-indigo-400" /> : vaultStatus.has_biometric ? <Fingerprint className="h-3 w-3 shrink-0" /> : <Lock className="h-3 w-3 shrink-0" />}
            <span className="truncate">{t('generated.conversationsidebar_private_7fb3')}</span>
            {!isGuest && vaultStatus.private_count > 0 && (
              <span className="ml-0.5 text-[10px] bg-indigo-500/20 text-indigo-400 px-1.5 rounded-full font-semibold">{vaultStatus.private_count}</span>
            )}
          </button>
          <button
            onClick={() => {
              if (isGuest) { onGuestBlock?.('bookmarks'); return; }
              handleTabSwitch('bookmarks');
            }}
            className={`flex items-center justify-center gap-1 rounded-md px-2 py-1.5 font-medium transition-all ${
              activeTab === 'bookmarks' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            style={{ fontSize: '11px', ...(isGuest ? { opacity: 0.4 } : {}) }}
            data-testid="tab-bookmarks"
          >
            <Bookmark className="h-3 w-3 shrink-0" />
            <span className="shrink-0">{t('sidebar.bookmarks', 'Saved')}</span>
            {displayCount > 0 && (
              <span className="shrink-0 text-[10px] bg-[var(--theme-accent)]/20 text-[var(--theme-accent)] px-1.5 rounded-full font-semibold leading-none whitespace-nowrap">{displayCount}</span>
            )}
          </button>
        </div>

        {/* Search (conversations tab only) */}
        {activeTab === 'conversations' && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text" value={searchQuery} onChange={handleSearchChange}
              placeholder={t('sidebar.search', 'Search conversations...')}
              autoComplete="off" name="conv-search"
              className="w-full rounded-lg bg-background/60 border border-border text-sm pl-9 pr-8 py-2 focus:outline-none focus:ring-1 focus:ring-[var(--theme-accent)] text-foreground placeholder:text-muted-foreground"
              data-testid="conversation-search-input"
            />
            {searchQuery && (
              <button onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2" data-testid="clear-search-btn">
                {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : <X className="h-3.5 w-3.5 text-muted-foreground" />}
              </button>
            )}
          </div>
        )}
      </div>
      {/* Content area */}
      {activeTab === 'conversations' ? (
        <>
          <ScrollArea className="flex-1 min-h-0">
            {/* pr-5 (20px) reserves generous space for the vertical
                scrollbar (Radix's 10px overlay AND wider native mobile
                scrollbars on Android/iOS) so row icons and the
                paginator's "Show N" selector never clash with the
                scrollbar column, even on narrow (w-80 / 320px) mobile
                sidebar sheets. */}
            <div className="pl-2 pr-5 py-2">
              {searchResults !== null && (
                <h3 className="px-2 pb-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider" data-testid="saved-conversations-title">
                  {t('sidebar.search_results', 'Search Results')}
                </h3>
              )}

              {displayConversations.length === 0 && (
                <p className="px-2 py-6 text-sm text-center text-muted-foreground/60" data-testid="no-conversations-message">
                  {searchResults !== null ? t('sidebar.no_results', 'No matching conversations') : t('sidebar.no_conversations', 'No saved conversations')}
                </p>
              )}

              {/* Pinned section */}
              {pinnedConversations.length > 0 && searchResults === null && (
                <>
                  <h3 className="px-2 pb-1.5 mt-2 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: 'var(--theme-accent)' }} data-testid="pinned-section-title">
                    <Pin className="h-3 w-3" style={{ fill: 'currentColor' }} /> {t('sidebar.pinned', 'Pinned')}
                  </h3>
                  {pinnedConversations.map((conv) => (
                    <SwipeableConversationItem
                      key={conv.conversation_id}
                      conversation={conv}
                      isActive={currentConversationId === conv.conversation_id}
                      onSelect={(id) => { clearSearch(); onLoadConversation(id); }}
                      onDelete={onDeleteConversation}
                      onMoveToVault={isGuest ? null : handleMoveToVault}
                      onTogglePin={handleTogglePin}
                      onRename={startRename}
                      isRenaming={renamingId === conv.conversation_id}
                      renameDraft={renameDraft}
                      setRenameDraft={setRenameDraft}
                      onCommitRename={commitRename}
                      onCancelRename={cancelRename}
                      isGuest={isGuest}
                      onGuestBlock={onGuestBlock}
                    />
                  ))}
                  {unpinnedConversations.length > 0 && (
                    <div className="mx-2 my-2 h-px" style={{ background: 'var(--glass-border, rgba(255,255,255,0.06))' }} />
                  )}
                </>
              )}

              {/* Recent / unpinned section */}
              {unpinnedConversations.length > 0 && searchResults === null && (
                <>
                  <h3 className="px-2 pb-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider" data-testid="recent-conversations-title">
                    {pinnedConversations.length > 0 ? t('sidebar.recent', 'Recent') : t('sidebar.saved', 'Saved Conversations')}
                  </h3>
                  {(() => {
                    const totalPages = Math.max(1, Math.ceil(unpinnedConversations.length / pageSize));
                    const safePage = Math.min(page, totalPages);
                    const start = (safePage - 1) * pageSize;
                    const rows = unpinnedConversations.slice(start, start + pageSize);
                    return rows.map((conv) => (
                      <SwipeableConversationItem
                        key={conv.conversation_id}
                        conversation={conv}
                        isActive={currentConversationId === conv.conversation_id}
                        onSelect={(id) => { clearSearch(); onLoadConversation(id); }}
                        onDelete={onDeleteConversation}
                        onMoveToVault={isGuest ? null : handleMoveToVault}
                        onTogglePin={handleTogglePin}
                        onRename={startRename}
                        isRenaming={renamingId === conv.conversation_id}
                        renameDraft={renameDraft}
                        setRenameDraft={setRenameDraft}
                        onCommitRename={commitRename}
                        onCancelRename={cancelRename}
                        isGuest={isGuest}
                        onGuestBlock={onGuestBlock}
                      />
                    ));
                  })()}

                  {/* Paginator — only shows when there's more than one page.
                      Alignment: `mx-2` matches the row icons' effective right
                      offset once the ScrollArea inner wrapper's `pr-3` is
                      applied, keeping the "Show N" selector visually aligned
                      with the trash-icon column above. */}
                  {unpinnedConversations.length > pageSize && (
                    <div
                      className="mx-2 my-3 px-2 py-2 rounded-lg flex items-center justify-between gap-2 text-xs"
                      style={{
                        background: 'var(--theme-surface-2, rgba(255,255,255,0.03))',
                        border: '1px solid var(--glass-border, rgba(255,255,255,0.06))',
                      }}
                      data-testid="conversation-paginator"
                    >
                      {(() => {
                        const totalPages = Math.max(1, Math.ceil(unpinnedConversations.length / pageSize));
                        const safePage = Math.min(page, totalPages);
                        return (
                          <>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setPage(Math.max(1, safePage - 1))}
                                disabled={safePage <= 1}
                                className="p-1 rounded hover:bg-[var(--theme-hover)] disabled:opacity-30"
                                aria-label={t('generated.conversationsidebar_previous_page_82d7')}
                                data-testid="paginator-prev"
                              >
                                <ChevronLeft className="h-3.5 w-3.5" />
                              </button>
                              <span
                                className="text-[11px] font-medium"
                                style={{ color: 'var(--theme-text-secondary, #94a3b8)' }}
                                data-testid="paginator-page-indicator"
                              >
                                {safePage} / {totalPages}
                              </span>
                              <button
                                onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                                disabled={safePage >= totalPages}
                                className="p-1 rounded hover:bg-[var(--theme-hover)] disabled:opacity-30"
                                aria-label={t('generated.conversationsidebar_next_page_1a33')}
                                data-testid="paginator-next"
                              >
                                <ChevronRight className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <label className="flex items-center gap-1.5 text-[11px]"
                                   style={{ color: 'var(--theme-text-secondary, #94a3b8)' }}>
                              {t('generated.conversationsidebar_show_547f')}
                              <select
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                className="rounded px-1.5 py-0.5 text-[11px] outline-none"
                                style={{
                                  background: 'var(--theme-surface, #1e293b)',
                                  color: 'var(--theme-text)',
                                  border: '1px solid var(--glass-border, rgba(255,255,255,0.1))',
                                }}
                                data-testid="paginator-page-size"
                              >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                              </select>
                            </label>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}

              {/* Search results (flat list, no pin sections) */}
              {searchResults !== null && displayConversations.map((conv) => (
                <SwipeableConversationItem
                  key={conv.conversation_id}
                  conversation={conv}
                  isActive={currentConversationId === conv.conversation_id}
                  onSelect={(id) => { clearSearch(); onLoadConversation(id); }}
                  onDelete={onDeleteConversation}
                  onMoveToVault={isGuest ? null : handleMoveToVault}
                  onTogglePin={handleTogglePin}
                  onRename={startRename}
                  isRenaming={renamingId === conv.conversation_id}
                  renameDraft={renameDraft}
                  setRenameDraft={setRenameDraft}
                  onCommitRename={commitRename}
                  onCancelRename={cancelRename}
                  isGuest={isGuest}
                  onGuestBlock={onGuestBlock}
                />
              ))}
            </div>
          </ScrollArea>

          {conversations.length > 0 && searchResults === null && (
            <div className="p-4 pb-4 border-t border-border mt-auto">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-2" data-testid="delete-all-conversations-button">
                    <Trash2 className="h-4 w-4" /> {t('sidebar.delete_all', 'Delete All')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-card border-border">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-foreground">{t('chat.delete_conv_title', 'Delete all conversations?')}</AlertDialogTitle>
                    <AlertDialogDescription className="text-muted-foreground">{t('chat.delete_conv_desc', 'This will permanently delete all your saved conversations.')}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-border text-foreground">{t('chat.cancel', 'Cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={onDeleteAllConversations} className="bg-red-600 hover:bg-red-700">{t('chat.delete', 'Delete')}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </>
      ) : activeTab === 'private' ? (
        /* Private vault tab */
        (<ScrollArea className="flex-1 min-h-0">
          <div className="pl-2 pr-5 py-2" data-testid="private-conversations-list">
            <div className="flex items-center justify-between px-2 pb-2 mt-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3 text-indigo-400" /> {t('generated.conversationsidebar_private_conversations_a959')}
              </h3>
              <button onClick={() => { setVaultUnlocked(false); setVaultConversations([]); setActiveTab('conversations'); }}
                className="text-xs text-muted-foreground hover:text-foreground transition flex items-center gap-1"
                data-testid="vault-lock-btn">
                <Lock className="h-3 w-3" /> {t('generated.conversationsidebar_lock_d7e8')}
              </button>
            </div>

            {vaultLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : vaultConversations.length === 0 ? (
              <div className="px-2 py-8 text-center">
                <Lock className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground/60">{t('generated.conversationsidebar_no_private_conversations_0da9')}</p>
                <p className="text-xs text-muted-foreground/40 mt-1">{t('generated.conversationsidebar_swipe_right_on_3d34')}</p>
              </div>
            ) : (
              vaultConversations.map((conv) => (
                <SwipeableConversationItem
                  key={conv.conversation_id}
                  conversation={conv}
                  isActive={currentConversationId === conv.conversation_id}
                  onSelect={(id) => onLoadConversation(id, true)}
                  onDelete={onDeleteConversation}
                  isPrivate
                  onMoveFromVault={handleMoveFromVault}
                />
              ))
            )}
          </div>
        </ScrollArea>)
      ) : (
        /* Bookmarks tab — with sub-tabs for Bookmarks, Groups, Labels, Shared */
        (<div className="flex flex-col flex-1 min-h-0">
          {/* Sub-tab toggle */}
          <div className="flex items-center gap-0.5 px-2 pt-2 pb-1 shrink-0 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            <button
              onClick={() => setBookmarkSubTab('bookmarks')}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors whitespace-nowrap shrink-0 ${
                bookmarkSubTab === 'bookmarks' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="subtab-bookmarks"
            >
              <Bookmark className="h-2.5 w-2.5" />
              {t('bookmarks.bookmarks_tab')}
            </button>
            <button
              onClick={() => setBookmarkSubTab('groups')}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors whitespace-nowrap shrink-0 ${
                bookmarkSubTab === 'groups' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="subtab-groups"
            >
              <FolderOpen className="h-2.5 w-2.5" />
              {t('bookmarks.groups_title')}
            </button>
            <button
              onClick={() => { setBookmarkSubTab('labels'); fetchAllLabels(); }}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors whitespace-nowrap shrink-0 ${
                bookmarkSubTab === 'labels' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="subtab-labels"
            >
              <Tag className="h-2.5 w-2.5" />
              {t('bookmarks.labels_title')}
            </button>
            <button
              onClick={() => { setBookmarkSubTab('shared'); refreshSavedShares(); }}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors whitespace-nowrap shrink-0 ${
                bookmarkSubTab === 'shared' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="subtab-shared"
            >
              <Share2 className="h-2.5 w-2.5" />
              {t('bookmarks.shared_tab')}
            </button>
            {bookmarkSubTab === 'bookmarks' && (
              <button onClick={refreshBookmarks} className="ml-auto text-muted-foreground hover:text-foreground transition p-1 shrink-0" data-testid="refresh-bookmarks-btn">
                {loadingBookmarks ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="text-[10px]">{t('sidebar.refresh', 'Refresh')}</span>}
              </button>
            )}
          </div>
          {bookmarkSubTab === 'bookmarks' ? (
            /* My Bookmarks */
            (<ScrollArea className="flex-1 min-h-0">
              <div className="pl-2 pr-5 py-2" data-testid="bookmarks-list">
                {loadingBookmarks && !bookmarksLoaded ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : bookmarks.length === 0 ? (
                  <div className="px-2 py-8 text-center" data-testid="no-bookmarks-message">
                    <Bookmark className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground/60">{t('sidebar.no_bookmarks', 'No bookmarks yet')}</p>
                    <p className="text-xs text-muted-foreground/40 mt-1">{t('sidebar.no_bookmarks_hint', 'Bookmark Eili\'s responses to save them here')}</p>
                  </div>
                ) : (
                  bookmarks.map((bm) => (
                    <div
                      key={bm.bookmark_id}
                      className="relative group/bm"
                      onContextMenu={(e) => handleBmContextMenu(e, bm)}
                      onTouchStart={(e) => startBmLongPress(e, bm)}
                      onTouchEnd={cancelBmLongPress}
                      onTouchMove={cancelBmLongPress}
                    >
                      {/* Label pills on bookmark */}
                      {bm.label_ids?.length > 0 && (
                        <div className="flex flex-wrap gap-1 px-3 pt-2">
                          {bm.label_ids.map((lid) => {
                            const lbl = allLabels.find((l) => l.label_id === lid);
                            if (!lbl) return null;
                            return (
                              <span
                                key={lid}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium"
                                style={{ background: `${lbl.color}20`, color: lbl.color }}
                              >
                                <span className="h-1.5 w-1.5 rounded-full" style={{ background: lbl.color }} />
                                {lbl.name}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {/* Group badge */}
                      {bm.group_id && (
                        <div className="px-3 pt-1">
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px]"
                            style={{ background: 'var(--glass-bg)', color: 'var(--theme-accent)' }}
                          >
                            <FolderOpen className="h-2 w-2" />
                            {allGroups.find((g) => g.group_id === bm.group_id)?.name || 'Group'}
                          </span>
                        </div>
                      )}
                      <SwipeableBookmarkItem
                        bookmark={bm}
                        onSelect={(convId, msgId) => onLoadConversation(convId, false, msgId)}
                        onDelete={removeBookmark}
                      />
                      {/* Actions button - always visible on right side */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchAllLabels();
                          fetchAllGroups();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setBmContextMenu({ x: rect.left - 120, y: rect.bottom + 4, bookmark: bm });
                        }}
                        className="absolute top-2 right-2 p-1 rounded-md transition-colors hover:bg-[var(--theme-hover)]"
                        style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
                        title={t('generated.conversationsidebar_more_actions_7f8d')}
                        data-testid={`bm-actions-${bm.bookmark_id}`}
                      >
                        <MoreHorizontal className="h-3 w-3" style={{ color: 'var(--theme-textSecondary)' }} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>)
          ) : bookmarkSubTab === 'groups' ? (
            /* Groups */
            (<BookmarkGroups onLoadConversation={onLoadConversation} />)
          ) : bookmarkSubTab === 'labels' ? (
            /* Labels */
            (<BookmarkLabels onLoadConversation={onLoadConversation} />)
          ) : (
            /* Shared with me */
            (<ScrollArea className="flex-1 min-h-0">
              <div className="pl-2 pr-5 py-2" data-testid="saved-shares-list">
                {savedShares.length === 0 ? (
                  <div className="px-2 py-8 text-center" data-testid="no-saved-shares-message">
                    <Share2 className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground/60">{t('generated.conversationsidebar_no_shared_content_4483')}</p>
                    <p className="text-xs text-muted-foreground/40 mt-1">{t('generated.conversationsidebar_save_responses_shared_3d3b')}</p>
                  </div>
                ) : (
                  savedShares.map((share) => (
                    <div
                      key={share.save_id}
                      className="mb-1.5 rounded-xl border border-transparent hover:border-border bg-card hover:bg-muted/30 transition-colors cursor-pointer group relative"
                      data-testid={`saved-share-${share.save_id}`}
                    >
                      <div
                        className="px-3 py-2.5 select-none"
                        onClick={() => window.open(`/share/${share.share_id}`, '_self')}
                      >
                        {share.topic && (
                          <div className="flex items-center gap-1 mb-1">
                            <Tag className="h-2.5 w-2.5" style={{ color: 'var(--theme-accent)' }} />
                            <span className="text-[10px] font-medium" style={{ color: 'var(--theme-accent)' }}>{share.topic}</span>
                          </div>
                        )}
                        <p className="text-sm text-foreground line-clamp-2 leading-relaxed">
                          {share.content ? share.content.slice(0, 120) + (share.content.length > 120 ? '...' : '') : share.conversation_title || 'Shared response'}
                        </p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[10px] text-muted-foreground/50">
                            {share.created_at ? new Date(share.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); removeSavedShare(share.save_id); }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-destructive/10"
                            data-testid={`delete-saved-share-${share.save_id}`}
                          >
                            <Trash2 className="h-3 w-3 text-destructive/60" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>)
          )}
          {/* Bookmark Context Menu */}
          {bmContextMenu && (
            <div className="fixed inset-0 z-[200]" onClick={() => setBmContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setBmContextMenu(null); }}>
              <div
                className="fixed min-w-[180px] rounded-xl shadow-xl py-1.5 animate-in fade-in zoom-in-95 duration-150"
                style={{
                  top: Math.min(bmContextMenu.y, window.innerHeight - 200),
                  left: Math.min(bmContextMenu.x, window.innerWidth - 200),
                  background: 'var(--theme-surface)',
                  border: '1px solid var(--glass-border)',
                }}
                onClick={(e) => e.stopPropagation()}
                data-testid="bookmark-context-menu"
              >
                <button
                  onClick={() => { setMoveGroupTarget(bmContextMenu.bookmark); setBmContextMenu(null); }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors hover:bg-[var(--theme-hover)]"
                  style={{ color: 'var(--theme-text)' }}
                  data-testid="ctx-move-to-group"
                >
                  <FolderOpen className="h-3.5 w-3.5" style={{ color: 'var(--theme-accent)' }} />
                  {t('bookmarks.move_to_group_action')}
                </button>
                <button
                  onClick={() => { setLabelAssignTarget(bmContextMenu.bookmark); setBmContextMenu(null); }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors hover:bg-[var(--theme-hover)]"
                  style={{ color: 'var(--theme-text)' }}
                  data-testid="ctx-assign-labels"
                >
                  <Tag className="h-3.5 w-3.5" style={{ color: 'var(--theme-accent)' }} />
                  {t('bookmarks.assign_labels_action')}
                </button>
                <div className="h-px mx-3 my-1" style={{ background: 'var(--glass-border)' }} />
                <button
                  onClick={() => { setConfirmDeleteBookmark(bmContextMenu.bookmark.bookmark_id); setBmContextMenu(null); }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors hover:bg-[var(--theme-hover)]"
                  style={{ color: '#ef4444' }}
                  data-testid="ctx-delete-bookmark"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t('generated.conversationsidebar_delete_bookmark_8347')}
                </button>
              </div>
            </div>
          )}
          {/* Move to Group Modal */}
          <MoveToGroupModal
            open={!!moveGroupTarget}
            groups={allGroups}
            currentGroupId={moveGroupTarget?.group_id || null}
            onMove={(gid) => handleMoveBookmarkToGroup(moveGroupTarget.bookmark_id, gid)}
            onClose={() => setMoveGroupTarget(null)}
          />
          {/* Label Assign Modal */}
          <LabelAssignModal
            open={!!labelAssignTarget}
            bookmarkId={labelAssignTarget?.bookmark_id}
            currentLabelIds={labelAssignTarget?.label_ids || []}
            allLabels={allLabels}
            onClose={() => setLabelAssignTarget(null)}
            onUpdated={(newLabelIds) => {
              setBookmarks((prev) =>
                prev.map((b) => b.bookmark_id === labelAssignTarget?.bookmark_id ? { ...b, label_ids: newLabelIds } : b)
              );
            }}
          />
        </div>)
      )}
      {/* PIN Modal is now rendered in ChatPage to escape Sheet focus trap */}
      {/* Bookmark Delete Confirmation */}
      <AlertDialog open={!!confirmDeleteBookmark} onOpenChange={(open) => { if (!open) setConfirmDeleteBookmark(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('generated.conversationsidebar_remove_bookmark_83a1')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('generated.conversationsidebar_are_you_sure_7c19')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sidebar-bookmark-remove-cancel">{t('generated.conversationsidebar_cancel_f0e0')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { removeBookmark(confirmDeleteBookmark); setConfirmDeleteBookmark(null); }}
              data-testid="sidebar-bookmark-remove-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('generated.conversationsidebar_remove_7012')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
